import * as React from 'react';
import { translate } from 'react-i18next';
import * as c from 'models/constants';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { IStore } from 'redux/IStore';
import { RouteComponentProps } from 'react-router';
import { getShop } from 'redux/modules/shop/manageshop';
import {
	clearAppointmentsData,
	getPublicHolidaysByOfficeId,
	setAppointmentFiltersDateAndMember,
	setAppointmentFiltersMember,
	setAppointmentFiltersOffice,
} from 'redux/modules/shop/appointments';
import { ShopAppointmentsFilterHeader } from './FilterHeader';
import { arePropArraysEqual, getOffice, getStaff } from './helpers';
import moment = require('moment');
import { ErrorSummary, LoadingOverlayWrapper, PersonItem } from 'components';
import { IHoliday, IManualBookingModalProps } from 'models/shop/appointments';
import { IOffice, IOfficeTime } from 'models/shop/office';
import { ITranslateProps } from 'models/common';
import { IScheduleItem } from 'models/shop/schedule';
import { Schedule } from '../Common/Schedule';
import {
	normalizeBookingTimetablesMultipleResources,
	timeAsNumber,
	timeAsNumberRelative,
} from 'helpers/bookingTemplates';
import { getBookingTemplatesByOffice } from 'redux/modules/shop/bookingtemplates';
import { NoBookingTemplates, NoStaffMembers } from '../Common/BookingTemplatesProblems';
import {
	bookingTemplateResourceIds,
	setBookingTimetablesFiltersOfficeAppointmentsDate,
} from 'redux/modules/shop/bookingtimetables';
import {
	addURLSearchParams,
	dayMonthFormatter,
	deleteURLSearchParams,
	lowerFirstObjectPropertiesNames,
	readURLSearchParams,
	shouldComponentUpdate,
} from 'helpers/common';
import { OrderPaymentStatus, OrderStatus } from 'models';
import { IBookingTimeTableGroup } from 'models/shop/bookingtimetables';
import { closeModal, isModalOpened, showModal } from 'redux/modules/modal';
import { isAdmin, momentWithTimezone } from 'redux/modules/shop/helpers';
import renderIcon from 'helpers/renderIcon';
import { ManualBookingScreen } from './ManualBookingModal';

interface IRouterParams {
	id: string;
	code: string;
	officeId?: string | undefined;
}

interface IOwnProps extends RouteComponentProps<IRouterParams, {}> {}

type IProps = IOwnProps & ReturnType<typeof stateToProps> & ReturnType<typeof dispatchToProps> & ITranslateProps;

interface IState {
	highlightedGroupId?: string;
	memoizedItems: any;
	mobileTopCoordinateOffset: number;
	hideTopCoordinate: boolean;
}

// Function that checks whether today date is public holiday or not
function isTodayHoliday(holidays: IHoliday[], date: string): boolean {
	for (let index = 0; index < holidays.length; index++) {
		const currentlySelectedDate = new Date(date);
		const today = {
			currentDay: currentlySelectedDate.getDate(),
			currentMonth: currentlySelectedDate.getMonth() + 1,
			currentYear: currentlySelectedDate.getFullYear(),
		};
		const holidayDate = {
			holidayDay: new Date(holidays[index].date).getDate(),
			holidayMonth: new Date(holidays[index].date).getMonth() + 1,
			holidayYear: new Date(holidays[index].date).getFullYear(),
		};

		if (
			today.currentDay === holidayDate.holidayDay &&
			today.currentMonth === holidayDate.holidayMonth &&
			today.currentYear === holidayDate.holidayYear
		) {
			return true;
		}
	}
	return false;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class ShopAppointmentsComponent extends React.Component<IProps, IState> {
	topCoordinateRef: React.RefObject<any>;
	scheduleHolderRef: React.RefObject<any>;

	constructor(props) {
		super(props);

		this.state = {
			memoizedItems: null,
			mobileTopCoordinateOffset: 0,
			hideTopCoordinate: false,
		};

		this.changeOfficeId = this.changeOfficeId.bind(this);
		this.startAddAppointment = this.startAddAppointment.bind(this);
		this.handleManualBookingModalOpenClose = this.handleManualBookingModalOpenClose.bind(this);
		this.handleScroll = this.handleScroll.bind(this);
		this.topCoordinateRef = React.createRef();
		this.scheduleHolderRef = React.createRef();
	}

	private fetchEverything(resetShop = false, officeDiff?) {
		let membersOfficeId = undefined;
		const role = (this.props.userPreferences?.userRoles || []).find(
			(role) =>
				role.role === 'Member' && role?.type == 'Office' && role?.objInfo?.attributes?.ShopId === this.props.params?.id,
		);
		if (role) {
			membersOfficeId = role.objId;
		}

		let changedOffice = undefined;

		if (resetShop || (!this.props.offices && !this.props.isFetchingShop)) {
			this.props.clearAppointmentsData();
			const dateFromUrl = readURLSearchParams('calendarDate');
			const timeFromClicked = readURLSearchParams('time');
			const memberId = readURLSearchParams('calendarResourceId');

			if (dateFromUrl || timeFromClicked) {
				this.props.setAppointmentFiltersDateAndMember(
					dateFromUrl || timeFromClicked,
					memberId,
					this.props?.params?.officeId || undefined, // officeId needs to be set in filters to display calendar, otherwise calendar does not render if not set!
				);
			} else if (memberId) {
				this.props.setAppointmentFiltersMember(memberId, this.props?.params?.officeId || undefined); // officeId needs to be set in filters to display calendar, otherwise calendar does not render if not set!
			}

			this.props.getShop(this.props.params.id);
			this.props.getOfficeServices(this.props.params.officeId);
		} else if (this.props.offices?.length && !this.props.offices[0].staff) {
			this.props.getShop(this.props.params.id);
			this.props.getOfficeServices(this.props.params.officeId);
		} else if (this.props.offices?.length && !this.props.params.officeId) {
			const officeId =
				membersOfficeId || this.props.offices.find((off) => off.isDefault).id || this.props.offices[0].id;
			this.changeOfficeId(officeId);

			// if the current user is a member, select him in the staff dropdown
			const resourceId = this.props.offices
				.find((o) => o.id == officeId)
				?.staff.find((s) => s?.userId == this.props.userPreferences?.id)?.id;
			if (membersOfficeId && resourceId) {
				this.props.setOfficeAndMemberAndFetch(officeId, 'week', resourceId);
			}
			changedOffice = officeId;
		} else if (
			this.props.offices?.length &&
			!this.props.appointments &&
			!this.props.isFetchingAppointments &&
			!this.props.error
		) {
			const officeId =
				this.props.params.officeId || this.props.offices.find((off) => off.isDefault).id || this.props.offices[0].id;
			if (officeId !== this.props.params.officeId) {
				this.changeOfficeId(officeId);
				changedOffice = officeId;
			} else {
				this.props.setOfficeAndFetch(officeId);
				this.props.getOfficeServices(officeId);
			}
		}
		if (officeDiff || changedOffice) {
			this.props.getOfficeServices(changedOffice || this.props.params.officeId);
		}
	}

	private changeOfficeId(officeId: string, push = false) {
		const shopId = this.props.params.id;
		if (!officeId || this.props.params.officeId === officeId) {
			return;
		}
		const routerFn = this.props.router[push ? 'push' : 'replace'];
		routerFn('/shop/' + shopId + '/appointments/' + officeId);
	}

	/**
	 * Open the manual booking modal if it should be opened and close if it should be closed
	 */
	private handleManualBookingModalOpenClose() {
		if (this.props.office && this.props.params.officeId == this.props.office?.id) {
			if (readURLSearchParams('manual-booking') == 'true') {
				if (!this.props.isModalOpened('ManualBookingModal')) {
					const timezone = this.props.office?.zoneInfo;
					this.props.showManualBookingModal({
						resourceId: undefined,
						officeId: this.props.office.id,
						shopId: this.props.office.shopId,
						time: undefined,
						timezone: timezone,
						mode: undefined,
					});
				}
			} else {
				if (this.props.isModalOpened('ManualBookingModal')) {
					this.props.closeModal('ManualBookingModal');
				}
			}
		}
	}

	debounceTimer = null;

	public componentDidMount() {
		this.fetchEverything(true);

		if (this.props?.appointments?.length && this.props?.allBookingTimetables?.length) {
			const items = this.items;
			this.setState({ memoizedItems: items });
		}

		if (screen?.width <= 992) {
			window.addEventListener('scroll', this.handleScroll);
		}

		this.handleManualBookingModalOpenClose();
	}

	public shouldComponentUpdate(nextProps: Readonly<IProps>, nextState: Readonly<IState>): boolean {
		return shouldComponentUpdate(this.props, nextProps, this.state, nextState);
	}

	public componentDidUpdate(prevProps: IProps): void {
		const { params, office, offices } = this.props;

		if (this.haveItemPropsChanged(prevProps)) {
			const newItems = this.items;
			this.setState({ memoizedItems: newItems });
		}

		if (
			office &&
			office.id === params.officeId &&
			(!prevProps.office ||
				prevProps.office.id !== this.props.office.id ||
				prevProps.date !== this.props.date ||
				prevProps.mode !== this.props.mode)
		) {
			this.props.getPublicHolidaysByOfficeId(params.officeId);
			this.props.getTimetablesForOffice(this.props.office);
		}
		if (prevProps.params.id !== params.id) {
			return this.fetchEverything(true);
		} else if (!offices || !this.props.appointments) {
			return this.fetchEverything(false);
		}
		if (params.officeId !== prevProps.params.officeId) {
			this.fetchEverything(false, true);
			if (offices && params.officeId) {
				this.props.setOfficeAndFetch(params.officeId);
			}
		} else if (!params.officeId) {
			this.fetchEverything();
		}
		this.handleManualBookingModalOpenClose();
	}

	componentWillUnmount(): void {
		window.removeEventListener('scroll', this.handleScroll);
	}

	private handleScroll = () => {
		clearTimeout(this.debounceTimer);

		if (!this.state.hideTopCoordinate && this.state.mobileTopCoordinateOffset > window.scrollY) {
			this.setState({ hideTopCoordinate: true });
		}

		this.debounceTimer = setTimeout(() => {
			// Check if ok like this or document.body needs to be used for mobile (use )
			this.setState({ mobileTopCoordinateOffset: window.scrollY, hideTopCoordinate: false });
		}, 120);
	};

	private haveItemPropsChanged = (prevProps: Readonly<IProps>): boolean => {
		const { allBookingTimetables, appointments, bookingTemplates, mode, date, staff, filterStaffId } = this.props;
		const prev = prevProps || {};

		if (!Object.keys(prev)) {
			return false;
		}

		return (
			mode !== prevProps.mode ||
			date !== prevProps.date ||
			filterStaffId !== prevProps.filterStaffId ||
			!arePropArraysEqual(allBookingTimetables, prevProps.allBookingTimetables) ||
			!arePropArraysEqual(appointments, prevProps.appointments) ||
			!arePropArraysEqual(bookingTemplates, prevProps.bookingTemplates) ||
			!arePropArraysEqual(staff, prevProps.staff)
		);
	};

	private startAddAppointment(time: moment.Moment, resourceId: string, screen?: ManualBookingScreen) {
		const timezone = this.props?.office?.zoneInfo;
		addURLSearchParams('manual-booking', 'true');
		if (screen) {
			addURLSearchParams('screen', screen);
		}
		this.props.showManualBookingModal({
			resourceId,
			officeId: this.props.office.id,
			shopId: this.props.office.shopId,
			time: momentWithTimezone(timezone, time).toISOString(),
			timezone: timezone,
			mode: 'add',
		});
	}

	private get items(): IScheduleItem[] {
		const { appointments, date, mode, staff, office, filterStaffId, t } = this.props;
		if (!office) {
			return [];
		}
		if (mode === 'day' && !staff) {
			return [];
		}
		const timezone = office?.zoneInfo;

		const minDate = momentWithTimezone(timezone, date).startOf('days');
		const maxDate = minDate.clone().add(mode === 'day' ? 1 : 7, 'day');

		// ADD APPOINTMENTS
		const appointmentItems: IScheduleItem[] = [];
		for (const a of appointments || []) {
			if (!a.shopOrder || a.shopOrder.status === OrderStatus.Canceled || a.shopOrder.status === OrderStatus.Rejected) {
				continue;
			}
			const firstStartTime = momentWithTimezone(timezone, a.orderItem?.bookedOrderItemDatas?.[0]?.startTime).startOf(
				'day',
			);

			for (let i = 0; i < (a.orderItem?.bookedOrderItemDatas || []).length; i++) {
				const bookedOrderItemData = (a.orderItem?.bookedOrderItemDatas || [])[i];
				if (!bookedOrderItemData) {
					continue;
				}
				const startTime = momentWithTimezone(timezone, bookedOrderItemData.startTime);
				const endTime = momentWithTimezone(timezone, bookedOrderItemData.endTime);
				/*if (!startTime.isSame(endTime, 'days')) {
					continue;
				}*/
				if (!startTime.isBetween(minDate, maxDate, undefined, '[)')) {
					continue;
				}
				if (mode === 'week' && filterStaffId !== bookedOrderItemData.resourceId) {
					continue;
				}
				let column = 0;
				if (mode === 'day') {
					column = staff.findIndex((s) => s.id === bookedOrderItemData.resourceId);
				} else {
					column = startTime.diff(minDate, 'days');
				}
				if (column < 0) {
					continue;
				}
				let appointmentName;
				let serviceName;
				if (a.orderItem?.bookedOrderItemDatas?.length > 1 && bookedOrderItemData?.bookingName) {
					appointmentName = (
						<div className="appointment-name">
							{a?.order?.paymentStatus === OrderPaymentStatus.Verified ||
							a?.order?.message?.includes('Original paid order id') === true
								? renderIcon('appointmentPaid')
								: null}
							{a?.orderItem?.productName || 'Appointment'}
						</div>
					);
					serviceName = <div className="muted">{bookedOrderItemData?.bookingName}</div>;
				} else {
					appointmentName = (
						<div className="appointment-name">
							{a?.order?.paymentStatus === OrderPaymentStatus.Verified ||
							a?.order?.message?.includes('Original paid order id') === true
								? renderIcon('appointmentPaid')
								: null}
							{bookedOrderItemData?.bookingName || a?.orderItem?.productName || 'Appointment'}
						</div>
					);
				}

				const title = (
					<div>
						<div>
							<div className="appointment-topside">
								{appointmentName}
								<div className="appointment-icons">
									{a?.shopOrder?.note && a.shopOrder?.note?.length != 0 && renderIcon('notes')}
									{a?.orderItem?.recurringBookingId && renderIcon('recurringEvent')}
								</div>
							</div>
							{serviceName}
						</div>
						<div className="muted details">
							{startTime.format('HH:mm') +
								' - ' +
								endTime.format('HH:mm') +
								(a.customer?.name ? ' • ' + a.customer.name : '')}
						</div>
					</div>
				);
				const appointmentBookingTemplate = this.props.bookingTemplates.find((bt) => bt.sku == a.orderItem.productSKU);
				const highlightGroupId = a.shopOrder?.id;

				appointmentItems.push({
					type: 'event',
					atomic: true,
					column,
					start: Math.max(timeAsNumberRelative(startTime, firstStartTime), 0),
					// If end time is in the next day, end will be smaller than start
					end: Math.min(timeAsNumberRelative(endTime, firstStartTime), 23 + 59 / 60),
					startTimeMoment: startTime,
					endTimeMoment: endTime,
					onClick: (rescheduleMode, rescheduleProps) => {
						const appointment = a;
						if (rescheduleMode == true) {
							// If drag and dropping existing appointment
							this.props.showManualBookingModal({
								mode: 'edit',
								resourceId: a?.orderItem?.bookedOrderItemDatas?.[0]?.resourceId,
								officeId: this.props.office.id,
								shopId: this.props.office.shopId,
								time: appointment.orderItem.productSchedule,
								timezone: timezone,
								appointment,
								updateOnlySelectedItem: true,
								rescheduleProps: {
									...rescheduleProps,
									orderItemId: appointment.orderItem.id,
									bookedOrderItemDataId: bookedOrderItemData.id,
								},
							});
						} else {
							// If clicking on existing appointment
							this.props.showManualBookingModal({
								mode: 'edit',
								resourceId: a?.orderItem?.bookedOrderItemDatas?.[0]?.resourceId,
								officeId: this.props.office.id,
								shopId: this.props.office.shopId,
								time: appointment.orderItem.productSchedule,
								timezone: timezone,
								appointment,
							});
						}
					},
					onMouseEnter: () => this.setState({ highlightedGroupId: highlightGroupId }),
					onMouseLeave: () =>
						this.setState((prevState) =>
							prevState.highlightedGroupId == highlightGroupId ? { highlightedGroupId: undefined } : {},
						),
					title,
					dragConstraints: {
						resource: appointmentBookingTemplate?.resources.map((r) => (typeof r == 'string' ? r : r.id)),
					},
					resourceId: bookedOrderItemData.resourceId,
					highlightGroupId,
				});
			}
		}

		const addTimeTable = (
			workTime: IBookingTimeTableGroup,
			type: 'empty' | 'available' | 'event' | 'partial',
			other: Partial<IScheduleItem>,
		) => {
			const startTime = momentWithTimezone(timezone, workTime.startTime).startOf('day');
			const column =
				mode === 'week' ? startTime.diff(minDate, 'days') : staff.findIndex((s) => s.id === workTime.resourceId);
			const startTimeMoment = momentWithTimezone(timezone, workTime.startTime);
			const endTimeMoment = momentWithTimezone(timezone, workTime.endTime);

			if (column >= 0) {
				availableItems.push({
					type,
					column,
					start: timeAsNumber(startTimeMoment),
					end: timeAsNumber(endTimeMoment),
					startTimeMoment,
					endTimeMoment,
					...other,
				});
			}
		};

		// ADD STAFF WORK TIMES (white)
		const availableItems: IScheduleItem[] = [];

		const addWorkTime = (workTime: IBookingTimeTableGroup, containsPartiallyUnavailable: boolean) =>
			addTimeTable(workTime, 'available', {
				onClick: (t: moment.Moment, screen?: ManualBookingScreen) =>
					this.startAddAppointment(t, workTime.resourceId, screen),
				atomic: c.SPLIT_TIME_INTERVAL_MINUTES / 60,
				resourceId: workTime?.resourceId,
				containsPartiallyUnavailable,
			});

		const addCustomEvent = (workTime: IBookingTimeTableGroup) =>
			addTimeTable(workTime, 'event', {
				title: (
					<div className={'custom custom-' + workTime.name}>
						<div>
							{workTime.name === '0' || workTime.name === '1' || workTime.name === '2' || workTime.name === '3'
								? t(`Shop_Appointments_TimeSlotType_${workTime.name}`)
								: workTime.name}
						</div>
						<div className="muted details">
							{momentWithTimezone(timezone, workTime.startTime).format('HH:mm') +
								' - ' +
								momentWithTimezone(timezone, workTime.endTime).format('HH:mm')}
						</div>
					</div>
				),
				onClick: (_rescheduleMode, rescheduleProps) => {
					addURLSearchParams('screen', 'add-edit-blocked-time');
					this.props.showManualBookingModal({
						mode: 'editBlockedTime',
						resourceId: workTime.resourceId,
						officeId: this.props.office.id,
						shopId: this.props.office.shopId,
						time: workTime.startTime,
						endTime: workTime.endTime,
						timezone: timezone,
						timetables: workTime.bookingTimetables,
						description: workTime.description,
						rescheduleProps,
					});
				},
				resourceId: workTime.resourceId,
				atomic: true,
				dragConstraints: { day: 'same', resource: 'same' },
			});

		if (!this.props.isFetchingTimetables && this.props.bookingTimetables?.length) {
			const filteredTimeTables = this.props.bookingTimetables.filter((tt) => {
				if (mode === 'week' && tt.resourceId !== filterStaffId) {
					return false;
				}
				if (!moment(tt.startTime).isBetween(minDate, maxDate, undefined, '[)')) {
					return false;
				}
				return true;
			});

			const filteredPartiallyUnavailableTimeTables = this.props?.partiallyUnavailableTimetables.filter((putt) => {
				if (mode === 'week' && putt.resourceId !== filterStaffId) {
					return false;
				}
				if (!moment(putt.startTime).isBetween(minDate, maxDate, undefined, '[)')) {
					return false;
				}
				return true;
			});

			const filteredUnavailableTimeTables = this.props?.unavailableTimeTables.filter((putt) => {
				if (mode === 'week' && putt.resourceId !== filterStaffId) {
					return false;
				}
				if (!moment(putt.startTime).isBetween(minDate, maxDate, undefined, '[)')) {
					return false;
				}
				return true;
			});

			filteredTimeTables.push(...filteredPartiallyUnavailableTimeTables, ...filteredUnavailableTimeTables);

			const timetablesByResource = normalizeBookingTimetablesMultipleResources(filteredTimeTables, timezone);

			for (const resourceId in timetablesByResource.available) {
				const groups = timetablesByResource.available[resourceId];

				for (const group of groups) {
					let containsPartiallyUnavailable: boolean = false;
					// If partial array contains any entries then compare and add class with partial background image
					if (timetablesByResource?.partial?.[resourceId]?.length) {
						timetablesByResource.partial[resourceId].forEach((partialTime) => {
							// Partial time will shrinks when you add a manual appointment over it
							if (
								moment(partialTime.startTime).isSameOrAfter(moment(group.startTime)) &&
								moment(partialTime.endTime).isSameOrBefore(moment(group.endTime))
							) {
								containsPartiallyUnavailable = true;
							}
						});
					}

					addWorkTime(group, containsPartiallyUnavailable);
				}
			}

			for (const resourceId in timetablesByResource.unavailable) {
				const groups = timetablesByResource.unavailable[resourceId];
				for (const group of groups) {
					addCustomEvent(group);
				}
			}
		}

		// ADD OFFICE OPENING HOURS (dotted)
		const emptyItems: IScheduleItem[] = [];
		const getOfficeTimeForDay = (d: 1 | 2 | 3 | 4 | 5 | 6 | 7): IOfficeTime | undefined => {
			const csday = d === 7 ? 0 : d;
			return (office.officeTimes || []).map(lowerFirstObjectPropertiesNames).find((ot) => ot.day === csday);
		};
		const addEmptyTime = (column: number, workTime: IOfficeTime) =>
			emptyItems.push({
				type: 'empty',
				column,
				start: timeAsNumber(momentWithTimezone(timezone, workTime.openTime)),
				end: timeAsNumber(momentWithTimezone(timezone, workTime.closeTime)),
				startTimeMoment: momentWithTimezone(timezone, workTime.openTime),
				endTimeMoment: momentWithTimezone(timezone, workTime.closeTime),
				atomic: true,
			});
		if (mode === 'day') {
			const workTime = getOfficeTimeForDay(minDate.isoWeekday() as any);
			if (workTime) {
				for (let column = 0; column < staff.length; column++) {
					addEmptyTime(column, workTime);
				}
			}
		} else {
			for (let column = 0; column < 7; column++) {
				const workTime = getOfficeTimeForDay(minDate.clone().add(column, 'days').isoWeekday() as any);
				if (workTime) {
					addEmptyTime(column, workTime);
				}
			}
		}

		const allItems = [...appointmentItems, ...availableItems, ...emptyItems];

		if (!allItems.length) {
			allItems.push({
				type: 'empty',
				column: 0,
				start: 9,
				end: 17,
				atomic: true,
			});
		}

		return allItems;
	}

	private get noServicesForFilter(): boolean | undefined {
		const { bookingTemplates, filterStaffId, fetchingBookingTemplates } = this.props;
		if (fetchingBookingTemplates || !bookingTemplates) {
			return undefined;
		}
		if (filterStaffId) {
			return !bookingTemplates.some((bt) => bookingTemplateResourceIds(bt).includes(filterStaffId));
		} else {
			return !bookingTemplates.length;
		}
	}

	private get isFetching(): boolean {
		return (
			this.props.fetchingBookingTemplates ||
			this.props.isFetchingShop ||
			this.props.isFetchingAppointments ||
			this.props.isFetchingTimetables
		);
	}

	public render() {
		const { mode, staff, date, params, filterStaffId, isFetchingShop, userPreferences, office } = this.props;
		const timezone = office?.zoneInfo;
		const today = momentWithTimezone(timezone);

		const isSiteAdmin: boolean = userPreferences.isAdministrator;
		const isShopOrOfficeAdmin: boolean =
			isAdmin(params.id, userPreferences.userRoles) || isAdmin(params.officeId, userPreferences.userRoles);

		let headerItems: JSX.Element[] = [];
		let columnWidth = 257;

		if (mode === 'week') {
			columnWidth = screen?.width >= 1920 ? Math.round((screen.width - 90) / 7) : 257; // Divide by number of days in week
			const styling = { width: `${columnWidth}px` };

			for (let i = 0; i < 7; i++) {
				const dayDate = momentWithTimezone(timezone, date).add(i, 'days');
				let className = '';
				if (dayDate.isSame(today, 'date')) {
					className = 'today';
				} else if (dayDate.isBefore(today, 'date')) {
					className = 'past';
				}
				headerItems.push(
					<div key={i} style={styling}>
						<span className={className}>
							<strong>{dayDate.format('dd')}</strong> {dayMonthFormatter(dayDate)}
						</span>
					</div>,
				);
			}
		} else {
			columnWidth =
				screen?.width >= 1920 && this.props?.staff?.length > 0 && this.props?.staff?.length < 8
					? Math.round((screen.width - 90) / this.props.staff.length)
					: 257; // Divide by amount of staff members
			const styling = { width: `${columnWidth}px` };

			headerItems = (staff || []).map((u, i) => (
				<div key={i} style={styling}>
					<PersonItem user={u} limitTextLength={true} />
				</div>
			));
		}
		let schedule: JSX.Element;
		if (staff && !staff.length && !isFetchingShop) {
			schedule = (
				<NoStaffMembers
					shopId={params.id}
					officeId={params.officeId}
					hideButton={!isSiteAdmin && !isShopOrOfficeAdmin}
				/>
			);
		} else if (this.noServicesForFilter) {
			schedule = (
				<NoBookingTemplates
					shopId={params.id}
					officeId={params.officeId}
					memberId={filterStaffId}
					hideButton={!isSiteAdmin && !isShopOrOfficeAdmin}
				/>
			);
		} else {
			const items = this.state.memoizedItems || [];

			schedule = (
				<LoadingOverlayWrapper active={this.isFetching} className="scheduleHolder">
					<div className="scheduleTableHolderWrapper" ref={this.scheduleHolderRef}>
						<div
							className="topCoordinate"
							ref={this.topCoordinateRef}
							style={{
								top: `${this.state.mobileTopCoordinateOffset}px`,
								visibility: this.state.hideTopCoordinate ? 'hidden' : 'visible',
							}}
						>
							{headerItems}
						</div>
						<Schedule
							items={items}
							step={c.MIN_TIME_INTERVAL_MINUTES / 60}
							split={c.SPLIT_TIME_INTERVAL_MINUTES / 60}
							start={items.reduce((acc, val) => (val.start < acc ? val.start : acc), 24)}
							topCoordinateRef={this.topCoordinateRef}
							today={momentWithTimezone(timezone, date).isSame(today, 'date')}
							highlightedGroupId={this.state?.highlightedGroupId}
							timezone={timezone}
							columnWidth={columnWidth}
						/>
					</div>
				</LoadingOverlayWrapper>
			);
		}
		return (
			<div className="topPadding shopAppointments">
				<ErrorSummary error={this.props.error} message={this.props.message} />
				<ShopAppointmentsFilterHeader
					timezone={timezone}
					section="appointments"
					officeId={params.officeId}
					changeOfficeId={this.changeOfficeId}
					changeMemberId={this.props.setAppointmentFiltersMember}
					publicHolidays={this.props.publicHolidays}
					isTodayHoliday={isTodayHoliday}
				/>
				{schedule}
			</div>
		);
	}
}

const stateToProps = (state: IStore, ownProps: IOwnProps) => {
	const bookingTemplates = (state.shopBookingTemplates.services || []).filter(
		(bt) => bt.officeId === ownProps.params.officeId,
	);
	const bookingTemplatesIds = bookingTemplates.map((bt) => bt.id);
	const bookingTimetables = (state.bookingTimetables.items || []).filter((btt) =>
		bookingTemplatesIds.includes(btt.bookingTemplateId),
	);
	// TASK 8507/8509 Partially unavailable and unavailable time tables no longer have booking template id's on them
	const partiallyUnavailableTimetables = (state.bookingTimetables.items || []).filter(
		(btt) => JSON.parse(btt.entityId).type === 'PartiallyUnavailableTimeTable',
	);
	const unavailableTimeTables = (state.bookingTimetables.items || []).filter(
		(btt) => JSON.parse(btt.entityId).type === 'UnavailableTimeTable',
	);

	return {
		// shop: state.manageShop,
		offices: state.manageShop?.shop?.offices,
		mode: state.appointments.mode || 'day',
		date: state.appointments.date,
		staff: getStaff(state, state.appointments.officeId),
		filterStaffId: state.appointments.memberId,
		isFetchingShop: state.manageShop.isFetching || state.manageShop.error,
		appointments: state.appointments.items,
		error: state.appointments.error,
		message: state.appointments.message,
		isFetchingAppointments: state.appointments.isFetching,
		isFetchingTimetables: state.bookingTimetables.isFetching,
		office: getOffice(state, state.appointments.officeId),
		fetchingBookingTemplates: state.shopBookingTemplates.isFetching,
		allBookingTimetables: state.bookingTimetables.items,
		bookingTimetables,
		partiallyUnavailableTimetables,
		unavailableTimeTables,
		bookingTemplates,
		publicHolidays: state.appointments.publicHolidays,
		userPreferences: state.userPreferences,
	};
};

const dispatchToProps = (dispatch: Dispatch<IStore>) => ({
	getShop: (id: string) => {
		dispatch(getShop(id, false, true));
	},
	getOfficeServices: (officeId: string) => {
		dispatch(getBookingTemplatesByOffice(officeId, true, undefined, undefined, true, true));
	},
	showManualBookingModal: (modalProps: IManualBookingModalProps) =>
		dispatch(showModal('ManualBookingModal', modalProps)),
	getTimetablesForOffice: (office: IOffice) => dispatch(setBookingTimetablesFiltersOfficeAppointmentsDate(office.id)),
	setOfficeAndFetch: (officeId: string, mode?: any) =>
		dispatch((dis, getState) =>
			dis(
				setAppointmentFiltersOffice(
					officeId,
					mode || (getState().appointments.memberId ? 'week' : 'day'),
					getState().appointments.memberId,
				),
			),
		),
	setOfficeAndMemberAndFetch: (officeId: string, mode?: any, memberId?: string) => {
		if (memberId) {
			addURLSearchParams('calendarResourceId', memberId);
		} else {
			deleteURLSearchParams(['calendarResourceId'], memberId);
		}
		addURLSearchParams('mode', mode);
		return dispatch(setAppointmentFiltersOffice(officeId, mode, memberId));
	},
	clearAppointmentsData: () => dispatch(clearAppointmentsData()),
	setAppointmentFiltersMember: (memberId: string, officeId?: string) => {
		if (memberId) {
			addURLSearchParams('calendarResourceId', memberId);
		} else {
			deleteURLSearchParams(['calendarResourceId'], memberId);
		}
		return dispatch(setAppointmentFiltersMember(memberId, officeId));
	},
	setAppointmentFiltersDateAndMember: (date: string | Date, memberId: string, officeId: string) => {
		addURLSearchParams('calendarDate', new Date(date).toISOString());
		if (memberId) {
			addURLSearchParams('calendarResourceId', memberId);
		} else {
			deleteURLSearchParams(['calendarResourceId'], memberId);
		}
		dispatch(setAppointmentFiltersDateAndMember(date, memberId, officeId));
	},
	showModal: (modalType: string, modalProps: any) => dispatch(showModal(modalType, modalProps)),
	getPublicHolidaysByOfficeId: (officeId: string, year?: number) =>
		dispatch(getPublicHolidaysByOfficeId(officeId, year)),
	isModalOpened: (modalType: string) => dispatch(isModalOpened(modalType)),
	closeModal: (modalType: string) => dispatch(closeModal(modalType)),
});

export const ShopAppointments = connect(stateToProps, dispatchToProps)(ShopAppointmentsComponent);
